import {FilterKey} from 'components/Accommodation/OffersFilters/constants'
import {getMeta} from 'modules/meta/selectors'
import {getSearchParameters} from 'modules/sapiSearch/selectors'
import {getUrlParams} from 'modules/search/selectors'
import {createSelector} from 'reselect'
import {RootState} from 'store'

//  Direct import of selector module needed instead of useBrand hook in selector context where hooks cannot be used.
// eslint-disable-next-line @vio/custom-rules/prefer-usebrand-hook
import {getBrand} from '@vio/core/src/_web/brand/modules/selectors'
import {getSapiVariationsHeader} from '@vio/core/src/experiments/modules/selectors'
import {DEFAULT_PROVIDER_CODE} from '@vio/core/src/offer/business/provider'
import {Offer} from '@vio/core/src/offer/types/offer'
import {updateAllPrices} from '@vio/core/src/room/business/transformSapiRoomsResponse/decoratePrices'
import {getBrandHasRooms} from '@vio/core/src/room/business/utils/getBrandHasRooms'
import {getPriceSortConfigFromTaxConfig} from '@vio/core/src/sapi/utils'
import {
  getFrozenBookingsByRoomIds,
  pickFrozenBookingByItinerary
} from '@vio/shared/src/dealFreeze/modules/selector'

import {AccommodationState} from './slice'

const getAccommodationState = (state: RootState): AccommodationState =>
  state.accommodation

const getAccommodationHotelId = (state: RootState) =>
  state.accommodation?.hotelId

/**
 * Get active AP filters from overlay state
 */

export const getAccommodationPageOfferFilters = createSelector(
  [getUrlParams],
  ({offerFilters}) => (offerFilters?.split(',') || []) as FilterKey[]
)

/**
 * Checks whether accommodation params are different.
 * All extraneous parameters in the main search params are ignored.
 */
export const getAccommodationParamsChanged = createSelector(
  [getAccommodationPageOfferFilters],
  filters => !!filters.length
)

/**
 * Get active AP filters as SAPI parameters
 */
export const getAccommodationPageFiltersParameters = createSelector(
  [getAccommodationPageOfferFilters, getMeta],
  (filters, meta) =>
    filters.reduce(
      (acc, cur) => {
        if (cur === 'priceSort') {
          return {
            ...acc,
            offersSort: getPriceSortConfigFromTaxConfig(meta)
          }
        }
        if (cur === 'breakfastIncluded') {
          return {
            ...acc,
            filters: {
              ...acc.filters,
              amenities: ['breakfast']
            }
          }
        }
        return {
          ...acc,
          filters: {
            ...acc.filters,
            [cur]: true
          }
        }
      },
      {filters: {}}
    )
)

/**
 * Returns hotel ID for the currently viewed AP.
 * Takes it from the queryString params, hotelId is always the ID from AP path hotelSlug
 */
export const getAccommodationPageHotelId = createSelector(
  [getUrlParams, getAccommodationHotelId],
  ({hotelSlug, hotelId}, accommodationHotelId) =>
    (hotelSlug ? hotelId : null) ?? accommodationHotelId
)
/**
 * Get accommodation lightbox overlay state
 */
export const getIsLightboxOverlayVisible = createSelector(
  [getAccommodationState],
  accommodation => accommodation.lightbox.isOverlayOpen
)

/**
 * Get accommodation lightbox overlay initialized image
 */
export const getLightboxSelectedImage = createSelector(
  [getAccommodationState],
  accommodation => accommodation.lightbox.overlaySelectedImage
)

export const isRoomsViewExpanded = createSelector(
  [getAccommodationState],
  accommodation => accommodation.isRoomsViewExpanded
)

export const getShouldDisplayAlternativeOffers = createSelector(
  [getAccommodationState],
  accommodation => accommodation.shouldDisplayAlternativeOffers
)

/**
 * SSR-safe equivalent of `isVioOfferOrBundleSelected()` from utils.ts.
 * The util reads from `window.location` and returns `false` during SSR,
 * which causes hydration mismatches when used to choose between component
 * trees during render. Read from the Redux query string instead.
 */
export const getIsVioOfferOrBundleSelected = createSelector(
  [getUrlParams],
  ({clickedOffer, clickedBundle}): boolean =>
    Boolean(clickedOffer || clickedBundle)
)

export const getIsRoomsFiltersLoading = (state: RootState): boolean =>
  state.accommodation.isRoomsFiltersLoading

/**
 * Hotel-scoped so an errored poll from a previous hotel cannot leak into the
 * current page's error handling.
 */
export const getHasRoomsSearchErrored = (
  state: RootState,
  hotelId: string
): boolean =>
  Boolean(hotelId) && state.accommodation.roomsSearchErrorHotelId === hotelId

export const getRoomsTopOfferLock = (state: RootState) =>
  state.accommodation?.roomsTopOfferLock

export const getSrpEntryFilters = (state: RootState) =>
  state.accommodation?.srpEntryFilters

export const getHasOffersSearchErrored = (
  state: RootState,
  hotelId: string
): boolean =>
  Boolean(hotelId) && state.accommodation.offersSearchErrorHotelId === hotelId

// A stable reference prevents memoization issues down the line
const EMPTY_FILTERS = []

/**
 * Get active rooms filters from overlay state
 */
export const getRoomsActiveFilters = (state: RootState) => {
  if (!state?.search?.queryString) return []
  const {roomFilters} = getUrlParams(state)
  return roomFilters?.split(',') || EMPTY_FILTERS
}

/**
 * Returns the hotelEntity corresponding the opened AP
 */
export const getCurrentHotelOfferEntity = (state: RootState) =>
  state.sapiSearch.hotelOfferEntities?.[state.accommodation.hotelId]

/**
 * Returns if any Rooms Filters are selected
 */
export const getHasActiveRoomFilter = createSelector(
  [getRoomsActiveFilters],
  activeRoomsFilters => Boolean(activeRoomsFilters.length)
)

export const getMetaBrandHasRooms = createSelector([getBrand], brand =>
  getBrandHasRooms(brand)
)

export const getOfferCheckRoomsHeaders = createSelector(
  [getSapiVariationsHeader],
  headers => ({
    ...headers,
    'X-Offer-Check-Cache-Support': 'true'
  })
)

export const getSoldoutOfferIds = (state: RootState) =>
  state.accommodation?.soldoutOfferIds

export const getIsOfferSoldOut = createSelector(
  [getSoldoutOfferIds, (_, id: string) => id],
  (soldoutOfferIds, id) => soldoutOfferIds?.[id]
)

// added as part of feature-offer-check-selected-offer-ap
export const getIsProactiveOfferCheckResolved = (state: RootState) =>
  state.accommodation?.isProactiveOfferCheckResolved

export const getOfferCheckRetryableErrorId = (state: RootState) =>
  state.accommodation?.offerCheckRetryableErrorId

/* Converts a frozen booking to Offer type with minimum required fields */
export const getFrozenBookingOffer = createSelector(
  [
    getFrozenBookingsByRoomIds,
    getAccommodationPageHotelId,
    getMeta,
    getSearchParameters
  ],
  (
    frozenBookings,
    hotelId,
    {includeTaxes, includeLocalTaxes},
    {checkIn, checkOut, rooms}
  ) => {
    const matchingFrozenBooking = pickFrozenBookingByItinerary(
      frozenBookings ?? [],
      checkIn,
      checkOut
    )
    if (!matchingFrozenBooking) return {offer: null, bookingId: null}
    const {
      id: bookingId,
      prices = [],
      cancellationPenalties = [],
      merchantOfRecord,
      rooms: frozenRooms
    } = matchingFrozenBooking
    const params = {
      checkIn,
      checkOut,
      rooms,
      hotelId
    }
    const offer: Offer = {
      availableRooms: 0,
      canPayLater: false,
      cug: [],
      id: '',
      links: [],
      metadata: {feedID: ''},
      providerCode: DEFAULT_PROVIDER_CODE,
      mealPlan: frozenRooms?.[0]?.mealPlan || [],
      merchantOfRecord,
      // The deal is already booked; finalising it is always paid now.
      paymentMode: 'pay_now',
      cancellationPenalties,
      prices: prices.map(price =>
        updateAllPrices(price, params, {
          includeTaxes,
          includeLocalTaxes
        })
      )
    }
    return {offer, bookingId}
  }
)
